import React, { useState } from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'
import styles from './index.module.scss'
import dayjs from '../../utilities/dayjs'
import { generatePath, Link } from 'react-router-dom'
import useMutation from '../../hooks/useMutation'
import removeTicketsFromInvoice from '../../graphql/mutations/removeTicketsFromInvoice'
import { ROUTES } from '../../routes'
import { commaDeliminate, formatDollarAmount } from '../../utilities/stringUtilities'
import notify from '../../utilities/notify'
import { captureErrorAndNotify } from '../../utilities/errorHandlers'
import RemoveTicketModal from './remove-ticket-modal'
import Modal from '../modal/Modal'
import MarkdownRenderizer from '../markdown-renderizer'

export default function InvoiceLineItemsTable ({
  invoice,
  showActionColumn,
  showLineItemTotals,
  showDiscountAndTax,
  editRoute,
  refetchInvoice = () => null
}) {
  const [removeTicketId, setRemoveTicketId] = useState(undefined)

  let columnWidth = ['16%', '30%', '24%', '8%', '11%', '11%']

  if (showActionColumn) {
    columnWidth = ['8%', '28%', '18%', '6%', '9%', '9%']
  }

  const { mutate: removeTicketsMutation, isLoading: isRemovingTickets } = useMutation(removeTicketsFromInvoice, {
    onSuccess ({ invoice }) {
      closeModal()
      notify('success', 'Ticket removed from Invoice')
      refetchInvoice()
    },
    onError (error) {
      captureErrorAndNotify(error, 'Unable to remove tickets from invoice')
    }
  })

  function removeTicket (id) {
    removeTicketsMutation({ invoiceID: invoice.id, tickets: [id] })
  }

  function closeModal () {
    setRemoveTicketId(undefined)
  }

  const colSpanFull = showActionColumn ? 7 : 6

  return (
    <>
      <Modal isOpen={removeTicketId != null}>
        {removeTicketId != null && (
          <RemoveTicketModal
            ticketId={removeTicketId}
            onClose={closeModal}
            onSave={removeTicket}
            isLoading={isRemovingTickets}
          />
        )}
      </Modal>
      <div className={styles.ticketFeeTableContainer}>
        <div className={styles.tableScroll}>
          <table className={cn('table', styles.ticketFeeTable)}>
            <colgroup>
              <col style={{ width: columnWidth[0] }} />
              <col style={{ width: columnWidth[1] }} />
              <col style={{ width: columnWidth[2] }} />
              <col style={{ width: columnWidth[3] }} />
              <col style={{ width: columnWidth[4] }} />
              <col style={{ width: columnWidth[5] }} />
              {showActionColumn && <col style={{ width: '22%' }} />}
            </colgroup>
            <thead>
              <tr>
                <th>Ticket ID</th>
                <th>Ticket Details</th>
                <th>Fee Type</th>
                <th>Qty</th>
                <th>Unit Price</th>
                <th>Amount</th>
                {showActionColumn && <th>Action</th>}
              </tr>
            </thead>
            <tbody>
              {!invoice?.tickets?.length && (
                <tr className={styles.noTicketsMessage}>
                  <td className={styles.noTicketsMessageCell} colSpan={colSpanFull}>
                    There are no tickets on this invoice.
                  </td>
                </tr>
              )}

              {invoice?.tickets?.map((ticket, ticketIndex) => {
                const isEvenRow = ticketIndex % 2 === 0
                const rowClass = isEvenRow ? styles.ticketRowEven : ''
                const fees = ticket?.ticketFees

                if (!fees?.length) {
                  return (
                    <tr key={ticket?.id} className={rowClass}>
                      <td>
                        <Link to={generatePath(ROUTES.ticket, { id: ticket?.id })}>
                          {ticket?.customId}
                        </Link>
                      </td>
                      <td>
                        <div>
                          <div className={styles.ticketTypeResourceType}>
                            {ticket?.ticketType?.name} - {ticket?.resourceType?.name}
                          </div>
                          <div>{ticket?.job?.addressline1}</div>
                          <div>{dayjs(ticket?.date).format('MMM D, YYYY')}</div>
                        </div>
                      </td>
                      <td colSpan={4} className='text-danger'>No Ticket Fees</td>
                      {showActionColumn && (
                        <td className={styles.actionCell}>
                          <div className={styles.actionButtons}>
                            <Link
                              className={cn('dis-btn dis-btn-sm dis-btn-primary-dk', styles.editFeesBtn)}
                              to={{
                                pathname: `${generatePath(ROUTES.ticket, { id: ticket?.id })}?scrollTo=fees`,
                                state: { returnTo: editRoute }
                              }}>
                              Edit Fees
                            </Link>
                            <button
                              onClick={() => setRemoveTicketId(ticket?.id)}
                              className={cn('dis-btn dis-btn-sm dis-btn-danger', styles.editFeesBtn)}>
                              Remove
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  )
                }

                const extraRows = showLineItemTotals ? (showDiscountAndTax ? 3 : 1) : 0
                const rowSpan = fees.length + extraRows

                return (
                  <React.Fragment key={ticket.id}>
                    {fees.map((ticketFee, index) => {
                      const isFirst = index === 0
                      const isLast = index === fees.length - 1

                      return (
                        <tr key={`${ticket.id}-fee-${index}`} className={rowClass}>
                          {isFirst && (
                            <>
                              <td rowSpan={rowSpan}>
                                <Link to={generatePath(ROUTES.ticket, { id: ticket?.id })}>
                                  {ticket?.customId}
                                </Link>
                              </td>
                              <td rowSpan={rowSpan}>
                                <div>
                                  <div className={styles.ticketTypeResourceType}>
                                    {ticket?.ticketType?.name} - {ticket?.resourceType?.name}
                                  </div>
                                  <div>{ticket?.job?.addressline1}</div>
                                  <div>{dayjs(ticket?.date).format('MMM D, YYYY')}</div>
                                </div>
                              </td>
                            </>
                          )}

                          <td className={cn({ [styles.lastTicketFee]: isLast && showLineItemTotals }, { [styles.feeSubRow]: !isFirst })}>
                            <span>
                              <p className={styles.feeType}>{ticketFee?.name}</p>
                              {ticketFee?.description && (
                                <div className={styles.feeDescription}>
                                  <MarkdownRenderizer markdownText={ticketFee.description} />
                                </div>
                              )}
                            </span>
                          </td>

                          <td className={cn({ [styles.lastTicketFee]: isLast && showLineItemTotals }, { [styles.feeSubRow]: !isFirst }, styles.quantityCell)}>
                            {ticketFee?.quantity}
                          </td>

                          <td className={cn({ [styles.lastTicketFee]: isLast && showLineItemTotals }, { [styles.feeSubRow]: !isFirst }, styles.priceCell)}>
                            ${commaDeliminate(ticketFee?.amount)}
                          </td>

                          <td className={cn({ [styles.lastTicketFee]: isLast && showLineItemTotals }, { [styles.feeSubRow]: !isFirst }, styles.amountCell)}>
                            ${commaDeliminate(ticketFee?.amountCents * ticketFee?.quantity / 100)}
                          </td>

                          {showActionColumn && isFirst && (
                            <td rowSpan={rowSpan} className={styles.actionCellTop}>
                              <div className={styles.actionButtons}>
                                <Link
                                  className={cn('dis-btn dis-btn-sm dis-btn-primary-dk', styles.editFeesBtn)}
                                  to={{
                                    pathname: `${generatePath(ROUTES.ticket, { id: ticket?.id })}?scrollTo=fees`,
                                    state: { returnTo: editRoute }
                                  }}>
                                  Edit Fees
                                </Link>
                                <button
                                  onClick={() => setRemoveTicketId(ticket?.id)}
                                  className={cn('dis-btn dis-btn-sm dis-btn-danger', styles.editFeesBtn)}>
                                  Remove
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      )
                    })}

                    {showLineItemTotals && (
                      <>
                        {showDiscountAndTax && (
                          <>
                            <tr key={`${ticket.id}-discount`} className={rowClass}>
                              <td className={styles.chargeTotals} colSpan={3}>Discount</td>
                              <td className={cn(styles.chargeTotals, 'text-left')}>
                                (${formatDollarAmount(ticket?.discountAndTax?.discountAmount)})
                              </td>
                            </tr>
                            <tr key={`${ticket.id}-tax`} className={rowClass}>
                              <td colSpan={3}>Tax</td>
                              <td className='text-left'>
                                ${formatDollarAmount(ticket?.discountAndTax?.taxAmount)}
                              </td>
                            </tr>
                          </>
                        )}
                        <tr key={`${ticket.id}-total`} className={rowClass}>
                          <td colSpan={3}><strong>Ticket Total</strong></td>
                          <td className='text-left'>
                            ${formatDollarAmount(ticket?.total)}
                          </td>
                        </tr>
                      </>
                    )}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}

InvoiceLineItemsTable.propTypes = {
  invoice: PropTypes.object.isRequired,
  showActionColumn: PropTypes.bool,
  showDiscountAndTax: PropTypes.bool.isRequired,
  showLineItemTotals: PropTypes.bool,
  editRoute: PropTypes.string,
  refetchInvoice: PropTypes.func
}
